<script setup lang="ts">
/**
 * Search input with a leading magnifier and a trailing X clear button
 * that appears once the user has typed something.
 *
 * Wraps AppInput so it inherits the same 44px touch target, focus ring,
 * and 16px-font-no-iOS-zoom rules. The clear button collapses the value
 * to '' without stealing focus, so the user can keep typing a fresh
 * query without re-tapping.
 *
 * Use this instead of bare `<AppInput type="search">` + an absolutely
 * positioned icon — list views collected the same pattern in six places.
 */
import { nextTick, ref } from 'vue'
import { Search, X } from '@lucide/vue'

import AppInput from './AppInput.vue'

interface Props {
  modelValue: string
  placeholder?: string
  /** Accessible name for screen readers. Required because list-header
   *  search fields have no visible label. Named `label` (not
   *  `label`) to avoid the kebab/camel collision with the native
   *  `aria-label` HTML attribute. */
  label: string
}
const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Search…',
})
const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const wrapper = ref<HTMLElement | null>(null)

function clear() {
  emit('update:modelValue', '')
  // Keep focus on the field so the user can keep typing. `mousedown.prevent`
  // on the button stops the pointer path from stealing focus; this covers
  // the keyboard path (the button unmounts via v-if once value is '',
  // which would otherwise drop focus to <body>).
  void nextTick(() => wrapper.value?.querySelector('input')?.focus())
}
</script>

<template>
  <div ref="wrapper" class="relative">
    <Search
      class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
      aria-hidden="true"
    />
    <AppInput
      :model-value="props.modelValue"
      type="search"
      :placeholder="placeholder"
      :aria-label="label"
      class="pl-9 pr-9"
      @update:model-value="(v) => emit('update:modelValue', String(v))"
    />
    <button
      v-if="props.modelValue.length > 0"
      type="button"
      :aria-label="`Clear ${label.toLowerCase()}`"
      class="absolute right-2 top-1/2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-focus-visible-managed
      @mousedown.prevent
      @click="clear"
    >
      <X class="size-4" aria-hidden="true" />
    </button>
  </div>
</template>
